import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Button } from './ui';
import { api } from '../lib/api';
import type { ApiResponse } from '../lib/types';

// Config de marca white-label (embutida ou dinâmica) vinda de /whitelabel/brands
export interface BrandConfig {
  label: string;
  domain?: string;
  script_name?: string;
  function_name: string;
  campaign_param: string;
  user_param: string;
  script_url: string;
  js_base_url: string;
  builtin?: boolean;
}

export const DEFAULT_BRAND: BrandConfig = {
  label: 'pixeldmp',
  function_name: 'setDmpData',
  campaign_param: 'poolId',
  user_param: 'signalId',
  script_url: 'https://audience.pixeldmp.com/pixel.min.js',
  js_base_url: 'https://audience.pixeldmp.com',
  builtin: true,
};

/** Busca as marcas e resolve a do label informado (fallback: pixeldmp). */
export function useBrand(label?: string | null): BrandConfig {
  const [brands, setBrands] = useState<BrandConfig[]>([]);
  useEffect(() => {
    api.get<ApiResponse<BrandConfig[]>>('/whitelabel/brands')
      .then((r) => setBrands(r.data || []))
      .catch(() => {});
  }, []);
  return brands.find((b) => b.label === (label || '')) || DEFAULT_BRAND;
}

export function CopyBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
  return (
    <div>
      <pre className="bg-slate-900 text-slate-100 rounded p-4 text-xs overflow-x-auto whitespace-pre mb-2"><code>{code}</code></pre>
      <Button type="button" variant="outline" onClick={copy}>{copied ? '✓ Copied!' : '⧉ Copy Code'}</Button>
    </div>
  );
}

function Hint({ children }: { children: ReactNode }) {
  return <p className="text-xs text-slate-500 mb-2">{children}</p>;
}

/** Snippet padrão da tag de uma marca (standard tag). */
export function brandTagSnippet(b: BrandConfig, poolId?: string | null) {
  const pool = poolId || '{campaignId}';
  return `<script type="text/javascript" src="${b.script_url}"></script>
<script>
    //Static
    ${b.function_name}({${b.campaign_param}: '${pool}', eventType: 'home'});

    //Dynamic
    ${b.function_name}({${b.campaign_param}: '${pool}', eventType: 'home/product/category/cart/purchase', itemId: '{list of itemIds}'});
</script>`;
}

/** Blocos de código de implementação da campanha (standard tag + single JS file). */
export function ImplementationCode({ poolId, jsId, brand }: { poolId?: string | null; jsId?: string | null; brand?: BrandConfig | null }) {
  const b = brand || DEFAULT_BRAND;
  return (
    <div>
      <h4 className="text-sm font-semibold text-ink-900 mb-1">Implementation Code {b.label !== 'pixeldmp' && <span className="text-xs font-normal text-slate-400">({b.label})</span>}</h4>
      <Hint>Choose one of the methods below to implement this campaign on your website.</Hint>

      <h5 className="text-sm font-semibold text-slate-600 mt-3 mb-1">Option 1: Standard Tag</h5>
      <Hint>Add the script tag and call manually.{!poolId && <> Replace <code className="bg-surface-muted px-1 rounded">{'{campaignId}'}</code> with the actual campaign ID.</>}</Hint>
      <CopyBlock code={brandTagSnippet(b, poolId)} />

      <h5 className="text-sm font-semibold text-slate-600 mt-4 mb-1">Option 2: Single JS File</h5>
      <Hint>Use a single script tag. The campaign JS ID will be filled automatically when the campaign is saved.</Hint>
      <CopyBlock code={`<script type="text/javascript" src="${b.js_base_url}/js/${jsId || '{campaignJsId}'}.js"></script>`} />
    </div>
  );
}
